import React, { ReactNode, useEffect } from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { createTheme, loadTheme } from "@fluentui/react";
import { SkeletonTheme } from "react-loading-skeleton";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

export const lightTheme = createTheme({
  palette: {
    themePrimary: "#005aa1",
    themeLighterAlt: "#f1f7fb",
    themeLighter: "#cadff0",
    themeLight: "#9fc5e3",
    themeTertiary: "#4f93c6",
    themeSecondary: "#156aac",
    themeDarkAlt: "#005291",
    themeDark: "#00457a",
    themeDarker: "#00335a",
    neutralLighterAlt: "#faf9f8",
    neutralLighter: "#f3f2f1",
    neutralLight: "#edebe9",
    neutralQuaternaryAlt: "#e1dfdd",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c6c4",
    neutralTertiary: "#a19f9d",
    neutralSecondary: "#605e5c",
    neutralPrimaryAlt: "#3b3a39",
    neutralPrimary: "#323130",
    neutralDark: "#201f1e",
    black: "#000000",
    white: "#ffffff",
    yellowDark: "#d29200",
    yellow: "#ffb900",
    yellowLight: "#fff100",
    orange: "#d83b01",
    orangeLight: "#ea4300",
    orangeLighter: "#ff8c00",
    redDark: "#a4262c",
    red: "#d13438",
    magentaDark: "#5c005c",
    magenta: "#b4009e",
    magentaLight: "#e3008c",
    purpleDark: "#32145a",
    purple: "#5c2d91",
    purpleLight: "#b4a0ff",
    blueDark: "#002050",
    blueMid: "#00188f",
    blue: "#0078d4",
    blueLight: "#00bcf2",
    tealDark: "#004b50",
    teal: "#008272",
    tealLight: "#00b294",
    greenDark: "#004b1c",
    green: "#107c10",
    greenLight: "#bad80a"
  }
});

export const darkTheme = createTheme({
  palette: {
    themePrimary: "#38a9ff",
    themeLighterAlt: "#02070a",
    themeLighter: "#091b29",
    themeLight: "#11334d",
    themeTertiary: "#226599",
    themeSecondary: "#3195e0",
    themeDarkAlt: "#4cb1ff",
    themeDark: "#68bdff",
    themeDarker: "#90cfff",
    neutralLighterAlt: "#212121",
    neutralLighter: "#2a2a2a",
    neutralLight: "#393939",
    neutralQuaternaryAlt: "#424242",
    neutralQuaternary: "#494949",
    neutralTertiaryAlt: "#686868",
    neutralTertiary: "#c8c8c8",
    neutralSecondary: "#d0d0d0",
    neutralPrimaryAlt: "#dadada",
    neutralPrimary: "#ffffff",
    neutralDark: "#f4f4f4",
    black: "#f8f8f8",
    white: "#171717",
    yellowDark: "#fef7b2",
    yellow: "#feee66",
    yellowLight: "#fdea3d",
    orange: "#e9835e",
    orangeLight: "#e9835e",
    orangeLighter: "#ffba66",
    redDark: "#f1bbbc",
    red: "#e37d80",
    magentaDark: "#eca5d1",
    magenta: "#d957a8",
    magentaLight: "#6b0043",
    purpleDark: "#c6b1de",
    purple: "#9470bd",
    purpleLight: "#341a51",
    blueDark: "#9abfdc",
    blueMid: "#a9d3f2",
    blue: "#5caae5",
    blueLight: "#004377",
    tealDark: "#9bd9db",
    teal: "#4cb4b7",
    tealLight: "#4cb4b7",
    greenDark: "#9fd89f",
    green: "#9fd89f",
    greenLight: "#294903"
  },
  effects: {
    elevation4:
      "0 1.6px 3.6px 0 rgba(248, 248, 248, 0.132), 0 0.3px 0.9px 0 rgba(248, 248, 248, 0.108)",
    elevation8:
      "0 3.2px 7.2px 0 rgba(248, 248, 248, 0.132), 0 0.6px 1.8px 0 rgba(248, 248, 248, 0.108)",
    elevation16:
      "0 6.4px 14.4px 0 rgba(248, 248, 248, 0.132), 0 1.2px 3.6px 0 rgba(248, 248, 248, 0.108)",
    elevation64:
      "0 25.6px 57.6px 0 rgba(248, 248, 248, 0.22), 0 4.8px 14.4px 0 rgba(248, 248, 248, 0.18)"
  }
});

type Props = {
  isDarkMode: boolean;
  children: ReactNode;
};

export const ThemeProvider = ({ isDarkMode, children }: Props) => {
  useEffect(() => {
    if (isDarkMode) {
      loadTheme(darkTheme);
    } else {
      loadTheme(lightTheme);
    }
  }, [isDarkMode]);

  return (
    <EmotionThemeProvider
      theme={isDarkMode ? { ...darkTheme, isDark: true } : lightTheme}
    >
      <StyledThemeProvider
        theme={isDarkMode ? { ...darkTheme, isDark: true } : lightTheme}
      >
        <SkeletonTheme
          color={isDarkMode ? darkTheme.palette.neutralLighter : undefined}
          highlightColor={
            isDarkMode ? darkTheme.palette.neutralQuaternaryAlt : undefined
          }
        >
          {children}
        </SkeletonTheme>
      </StyledThemeProvider>
    </EmotionThemeProvider>
  );
};
